/**
 * Media Stream Hook
 * Manages camera, microphone, and screen sharing
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  UseMediaStreamOptions,
  UseMediaStreamReturn,
} from '@/api/types/webrtc.types';

/**
 * Hook for managing media devices (camera, microphone, screen)
 *
 * @example
 * ```typescript
 * const {
 *   stream,
 *   isLoading,
 *   error,
 *   isAudioEnabled,
 *   isVideoEnabled,
 *   toggleAudio,
 *   toggleVideo,
 *   startScreenShare,
 *   stopScreenShare
 * } = useMediaStream({
 *   audio: true,
 *   video: true,
 *   autoStart: true
 * });
 * ```
 */
export function useMediaStream(
  options: UseMediaStreamOptions = {}
): UseMediaStreamReturn {
  const { audio = true, video = true, autoStart = false } = options;

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  const streamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const originalVideoTrackRef = useRef<MediaStreamTrack | null>(null);

  // Get user media (camera + mic)
  const startMedia = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if getUserMedia API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          'Camera/microphone access is not available. ' +
            'Please use HTTPS or access via localhost (http://localhost:3000). ' +
            'Current browsers block camera/microphone on HTTP sites for security.'
        );
      }

      console.log('📹 Requesting user media...');

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: audio || false,
        video: video || false,
      });

      console.log('✅ Got user media:', {
        audioTracks: mediaStream.getAudioTracks().length,
        videoTracks: mediaStream.getVideoTracks().length,
      });

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setIsAudioEnabled(true);
      setIsVideoEnabled(true);
    } catch (err) {
      console.error('❌ Failed to get user media:', err);

      const error = err as Error;

      // Provide user-friendly error messages
      if (error.name === 'NotAllowedError') {
        setError(
          new Error(
            'Camera or microphone permission denied. Please allow access and try again.'
          )
        );
      } else if (error.name === 'NotFoundError') {
        setError(new Error('No camera or microphone found on this device.'));
      } else if (error.name === 'NotReadableError') {
        setError(
          new Error(
            'Camera or microphone is already in use by another application.'
          )
        );
      } else {
        setError(error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [audio, video]);

  // Stop media stream
  const stopMedia = useCallback(() => {
    if (streamRef.current) {
      console.log('🛑 Stopping media stream');
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
      setStream(null);
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      screenStreamRef.current = null;
      setIsScreenSharing(false);
    }
  }, []);

  // Toggle audio track
  const toggleAudio = useCallback(() => {
    if (!streamRef.current) return;

    const audioTrack = streamRef.current.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setIsAudioEnabled(audioTrack.enabled);
      console.log(`🎤 Audio ${audioTrack.enabled ? 'enabled' : 'disabled'}`);
    }
  }, []);

  // Toggle video track
  const toggleVideo = useCallback(() => {
    if (!streamRef.current) return;

    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      setIsVideoEnabled(videoTrack.enabled);
      console.log(`📹 Video ${videoTrack.enabled ? 'enabled' : 'disabled'}`);
    }
  }, []);

  // Start screen sharing
  const startScreenShare = useCallback(async (): Promise<MediaStream> => {
    try {
      console.log('🖥️ Starting screen share...');

      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
        } as MediaTrackConstraints,
        audio: false, // Most browsers don't support system audio capture
      });

      console.log('✅ Got screen share stream');

      // Save original video track
      if (streamRef.current) {
        const videoTrack = streamRef.current.getVideoTracks()[0];
        if (videoTrack) {
          originalVideoTrackRef.current = videoTrack;
        }
      }

      // Replace video track with screen track
      const screenTrack = screenStream.getVideoTracks()[0];

      if (streamRef.current) {
        const videoTrack = streamRef.current.getVideoTracks()[0];
        if (videoTrack) {
          streamRef.current.removeTrack(videoTrack);
          videoTrack.stop();
        }
        streamRef.current.addTrack(screenTrack);
        setStream(new MediaStream(streamRef.current.getTracks()));
      }

      screenStreamRef.current = screenStream;
      setIsScreenSharing(true);

      // Handle user stopping screen share via browser UI
      screenTrack.onended = () => {
        console.log('🛑 Screen share stopped by user');
        stopScreenShare();
      };

      return screenStream;
    } catch (err) {
      console.error('❌ Failed to start screen share:', err);
      throw err;
    }
  }, []);

  // Stop screen sharing
  const stopScreenShare = useCallback(() => {
    if (!isScreenSharing) return;

    console.log('🛑 Stopping screen share');

    // Stop screen share tracks
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      screenStreamRef.current = null;
    }

    // Restore original video track
    if (streamRef.current && originalVideoTrackRef.current) {
      const screenTrack = streamRef.current.getVideoTracks()[0];
      if (screenTrack) {
        streamRef.current.removeTrack(screenTrack);
        screenTrack.stop();
      }

      streamRef.current.addTrack(originalVideoTrackRef.current);
      setStream(new MediaStream(streamRef.current.getTracks()));
      originalVideoTrackRef.current = null;
    }

    setIsScreenSharing(false);
  }, [isScreenSharing]);

  // Switch camera
  const switchCamera = useCallback(async (deviceId: string) => {
    if (!streamRef.current) return;

    try {
      console.log(`📹 Switching camera to: ${deviceId}`);

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });

      const videoTrack = streamRef.current.getVideoTracks()[0];
      const newVideoTrack = newStream.getVideoTracks()[0];

      if (videoTrack && newVideoTrack) {
        streamRef.current.removeTrack(videoTrack);
        videoTrack.stop();
        streamRef.current.addTrack(newVideoTrack);
        setStream(new MediaStream(streamRef.current.getTracks()));
        console.log('✅ Camera switched');
      }
    } catch (err) {
      console.error('❌ Failed to switch camera:', err);
      throw err;
    }
  }, []);

  // Switch microphone
  const switchMicrophone = useCallback(async (deviceId: string) => {
    if (!streamRef.current) return;

    try {
      console.log(`🎤 Switching microphone to: ${deviceId}`);

      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
        video: false,
      });

      const audioTrack = streamRef.current.getAudioTracks()[0];
      const newAudioTrack = newStream.getAudioTracks()[0];

      if (audioTrack && newAudioTrack) {
        streamRef.current.removeTrack(audioTrack);
        audioTrack.stop();
        streamRef.current.addTrack(newAudioTrack);
        setStream(new MediaStream(streamRef.current.getTracks()));
        console.log('✅ Microphone switched');
      }
    } catch (err) {
      console.error('❌ Failed to switch microphone:', err);
      throw err;
    }
  }, []);

  // Enumerate devices
  useEffect(() => {
    // Check if mediaDevices API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.warn('⚠️ navigator.mediaDevices API not available');
      return;
    }

    const enumerateDevices = async () => {
      try {
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        setDevices(deviceList);
        console.log('📱 Available devices:', {
          videoinput: deviceList.filter((d) => d.kind === 'videoinput').length,
          audioinput: deviceList.filter((d) => d.kind === 'audioinput').length,
          audiooutput: deviceList.filter((d) => d.kind === 'audiooutput')
            .length,
        });
      } catch (err) {
        console.error('❌ Failed to enumerate devices:', err);
      }
    };

    enumerateDevices();

    // Listen for device changes
    if (navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);

      return () => {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          enumerateDevices
        );
      };
    }
  }, []);

  // Auto-start if requested
  useEffect(() => {
    if (autoStart) {
      startMedia();
    }

    // Cleanup on unmount
    return () => {
      stopMedia();
    };
  }, []); // Empty deps - only run on mount/unmount

  return {
    stream,
    isLoading,
    error,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
    startMedia,
    stopMedia,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    devices,
    switchCamera,
    switchMicrophone,
  };
}
